import type { SorterResult } from "antd/lib/table/interface";
import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { DynamicPropertyGroupEntity } from "src/app/features/DynamicPropertyGroup/types/DynamicPropertyGroupEntity";
import { type ExtendedColumnsType, GenericTable, type ResourceTableProps } from "../../../../../components";
import {
    displayDateTimeValue,
    displayStatusValue,
} from "../../../../../components/Table/components/PropertyTable/transformations/cellTransformations";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";

export const DynamicPropertyGroupsTable: FC<ResourceTableProps<DynamicPropertyGroupEntity>> = ({
    onStateChanged,
    onRowSelectionChange,
    datasource,
    resource,
}) => {
    const { t } = useTranslation();
    const getColumnsConfig = useCallback(
        (sortInfo: SorterResult<DynamicPropertyGroupEntity>): ExtendedColumnsType<DynamicPropertyGroupEntity>[] => {
            return [
                {
                    title: t("dynamicPropertyGroup.id"),
                    key: "id",
                    dataIndex: "id",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "id" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicPropertyGroup.name"),
                    key: "name",
                    dataIndex: "name",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "name" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicPropertyGroup.used"),
                    key: "used",
                    dataIndex: "used",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "used" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicPropertyGroup.properties"),
                    key: "properties",
                    dataIndex: "properties",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "properties" ? sortInfo.order : null,
                },
                {
                    title: t("dynamicPropertyGroup.status"),
                    key: "status",
                    dataIndex: "status",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "status" ? sortInfo.order : null,
                    render: (value) => displayStatusValue(value),
                },
                {
                    title: t("dynamicPropertyGroup.changed"),
                    dataIndex: "changed",
                    key: "changed",
                    sorter: () => 0,
                    sortOrder: sortInfo.columnKey === "changed" ? sortInfo.order : null,
                    render: (value) => displayDateTimeValue(value),
                },
            ];
        },
        [t],
    );

    const navigate = useNavigate();
    const onRowClick = (record: DynamicPropertyGroupEntity) =>
        navigate(`${BASE_PATH}/${ResourceName.DYNAMIC_PROPERTY_GROUPS}/${FORM_ACTION.EDIT}/${record.id}`);

    return (
        (datasource && (
            <GenericTable<DynamicPropertyGroupEntity>
                getColumnsConfig={getColumnsConfig}
                datasource={datasource}
                name="dynamicPropertyGroups_table"
                onSelectionCallback={onRowSelectionChange}
                onStateChanged={onStateChanged}
                onRow={(record: DynamicPropertyGroupEntity) => {
                    return {
                        onClick: () => onRowClick(record),
                    };
                }}
                resource={resource}
            />
        )) ||
        null
    );
};
